import React from 'react';
import { MapPin, CurrencyDollar, Buildings, Briefcase } from '@phosphor-icons/react';
import type { IntlShape } from 'react-intl';
import {
  FILTER_CAT_COMPANY,
  FILTER_CAT_JOB,
  FILTER_CAT_LOCATION,
  FILTER_CAT_SALARY,
} from '../../constants/corePublicTheme';

export interface FilterCategory {
  id: string;
  label: string;
  icon?: React.ReactNode;
  colorClass: string;
  hasFilters: boolean;
}

export interface FilterCategoryButtonsProps {
  intl?: IntlShape;
  categories: FilterCategory[];
  activeCategory: string | null;
  onCategoryClick: (categoryId: string) => void;
  className?: string;
}

/**
 * Filter category buttons component
 * Shows category buttons (Location, Salary, Company, Job) that open filter modals
 * Designed with Apple design language: subtle shadows, smooth transitions, clean aesthetics
 */
export const FilterCategoryButtons: React.FC<FilterCategoryButtonsProps> = ({
  categories,
  activeCategory,
  onCategoryClick,
  className = '',
  intl: _intl,
}) => {
  return (
    <div className={`flex items-center gap-2 flex-nowrap min-w-0 ${className}`}>
      {categories.map((category) => {
        const isActive = activeCategory === category.id;
        const hasFilters = category.hasFilters;
        
        // lg–xl: icon-only to avoid stacking; xl+: icon + label. flex-nowrap keeps one row.
        const baseClasses = 'flex items-center justify-center xl:justify-start gap-2 px-3 py-2 xl:px-4 xl:py-2.5 rounded-full text-sm font-medium transition-all duration-300 ease-out cursor-pointer select-none border flex-shrink-0';
        
        let buttonClasses = baseClasses;

        if (isActive) {
          buttonClasses += ` ${category.colorClass} border-2 shadow-md dark:shadow-none`;
        } else if (hasFilters) {
          buttonClasses += ` ${category.colorClass} border shadow-sm dark:shadow-none`;
        } else {
          buttonClasses +=
            ' bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none';
        }

        buttonClasses += ' hover:shadow-md dark:hover:shadow-none hover:scale-[1.02] active:scale-[0.98]';
        
        return (
          <button
            key={category.id}
            onClick={() => onCategoryClick(category.id)}
            className={buttonClasses}
            aria-label={category.label}
            aria-pressed={isActive}
            style={{ transformOrigin: 'center' }}
          >
            <span className="flex-shrink-0" style={{ 
              opacity: isActive ? 1 : 0.9,
              transition: 'opacity 0.2s ease-out'
            }}>
              {category.icon}
            </span>
            <span className="hidden xl:inline whitespace-nowrap font-semibold">{category.label}</span>
            {hasFilters && (
              <span 
                className="hidden xl:inline ml-0.5 text-[10px] leading-none"
                style={{
                  opacity: isActive ? 0.9 : 0.7,
                  transition: 'opacity 0.2s ease-out'
                }}
                aria-hidden="true"
              >
                ●
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export interface FilterCategoryLabels {
  location: string;
  salary: string;
  company: string;
  job: string;
}

/**
 * Creates filter categories with pre-generated labels (for static SRP without IntlProvider).
 */
export function createFilterCategoriesFromLabels(
  labels: FilterCategoryLabels,
  filtersObj: Record<string, string[]>
): FilterCategory[] {
  return [
    {
      id: 'location',
      label: labels.location,
      icon: <MapPin weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_LOCATION,
      hasFilters: !!(filtersObj.location?.length || filtersObj.remoteness?.length),
    },
    {
      id: 'salary',
      label: labels.salary,
      icon: <CurrencyDollar weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_SALARY,
      hasFilters: !!(filtersObj.salaryRangeMin?.length || filtersObj.vacationWeeks?.length),
    },
    {
      id: 'company',
      label: labels.company,
      icon: <Buildings weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_COMPANY,
      hasFilters: !!(filtersObj.companyId?.length || filtersObj.companyIndustry?.length),
    },
    {
      id: 'job',
      label: labels.job,
      icon: <Briefcase weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_JOB,
      hasFilters: !!(
        filtersObj.jobType?.length ||
        filtersObj.normalisedRoleLevel?.length ||
        filtersObj.requiredLanguages?.length ||
        filtersObj.benefits?.length ||
        filtersObj.skillsAndTechnologies?.length ||
        filtersObj.field?.length ||
        filtersObj.educationLevel?.length ||
        filtersObj.peopleManagementTier?.length ||
        filtersObj.lastSeenMin?.length ||
        filtersObj.excludeSaved?.length ||
        filtersObj.excludeViewed?.length ||
        filtersObj.excludeApplied?.length
      ),
    },
  ];
}

/**
 * Helper function to create filter categories array
 */
export function createFilterCategories(
  intl: IntlShape,
  filtersObj: Record<string, string[]>
): FilterCategory[] {
  return createFilterCategoriesFromLabels(
    {
      location: intl.formatMessage({ id: 'search.refinements.location', defaultMessage: 'Location', description: 'Filter label for location selection in search refinements' }),
      salary: intl.formatMessage({ id: 'search.refinements.salary', defaultMessage: 'Salary', description: 'Filter label for salary range selection in search refinements' }),
      company: intl.formatMessage({ id: 'search.refinements.company', defaultMessage: 'Company', description: 'Filter label for company selection in search refinements' }),
      job: intl.formatMessage({ id: 'search.refinements.job', defaultMessage: 'Job', description: 'Filter label for job-related filters (type, title, etc.) in search refinements' }),
    },
    filtersObj
  );
}
